import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { reduxForm } from 'redux-form';
import styled, { css } from 'styled-components';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormUi, Flex, Button, Grid, Field, Segment, Menu } from 'nallian-shared-ui/lib/components';
import { TextAreaFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import Markdown from '../../../../portal/app/components/Markdown';

const CommunityTextDivider = styled.div`
  min-height: 100%;
  background: rgba(0, 0, 0, 0.2);
  width: 1px;
  margin: 0 2rem;
`;

const FullWidthSegment = styled(Segment)`
  min-width: 100%;
`;

const HiddenGridColumn = styled(Grid.Column)`
  ${props =>
    !props.active &&
    css`
      display: none !important;
    `}
`;

const CommunityTextForm = ({
  handleSubmit,
  submitting,
  languageOptions,
  onSubmit,
  formName,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [languages, setLanguages] = useState(languageOptions);

  const handleSetLanguages = useCallback(
    (_, { name }) => setLanguages(languages.map(hl => ({ ...hl, active: hl.id === name }))),
    [languages, setLanguages]
  );

  const activeLanguage = useMemo(() => languages.find(l => l.active)?.id, [languages]);

  const formValue = useSelector(state =>
    get(state, `form.${formName}.values.communityText.${activeLanguage}`)
  );

  return (
    <Flex fluid direction="column">
      <Menu pointing secondary>
        {languages.map(l => (
          <Menu.Item
            name={l.id}
            key={l.id}
            content={l.id}
            active={l.active}
            onClick={handleSetLanguages}
          />
        ))}
      </Menu>
      <Flex fluid>
        <FormUi fluid onSubmit={handleSubmit(onSubmit)}>
          <Grid>
            {languages.map(l => (
              <HiddenGridColumn active={l.active} key={l.id} mobile={16} tablet={16} computer={16}>
                <Field
                  fluid
                  disabled={submitting || readOnly}
                  name={`communityText.${l.id}`}
                  valueComponent={TextAreaFieldValueComponent}
                  rows={12}
                />
              </HiddenGridColumn>
            ))}
          </Grid>
          {!readOnly && (
            <Flex justifyContent="flex-end" margin="1rem 0 0">
              <Button
                btnprimary
                onClick={handleSubmit(onSubmit)}
                disabled={submitting}
                loading={submitting}
                icon="save"
                content={t('save')}
              />
            </Flex>
          )}
        </FormUi>
        <CommunityTextDivider />
        <Flex fluid>
          <FullWidthSegment fluid secondary>
            {formValue ? (
              <Flex fluid direction="column">
                <Markdown markdown={formValue} />
              </Flex>
            ) : (
              <Flex fluid>
                <i>{t('noCommunityTextYet')}</i>
              </Flex>
            )}
          </FullWidthSegment>
        </Flex>
      </Flex>
    </Flex>
  );
};

CommunityTextForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  languageOptions: PropTypes.array.isRequired,
  submitting: PropTypes.bool.isRequired,
  initialValues: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
};

const createDynamicReduxForm = (form, initialValues) => {
  return reduxForm({
    form,
    initialValues,
    validate: () => {},
    enableReinitialize: true,
    destroyOnUnmount: false,
  })(CommunityTextForm);
};

// eslint-disable-next-line react/prop-types
const DynamicFormWrapper = ({ type, initialValues, ...props }) => {
  const formName = `${type}TextForm`;
  const DynamicForm = createDynamicReduxForm(formName, initialValues);

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <DynamicForm {...props} formName={formName} />;
};

export default DynamicFormWrapper;
